import { RefCallback, useEffect, useState, useSyncExternalStore } from 'react'

export const createClearanceRegistry = (measureElement: (element: HTMLElement) => number) => {
  const elements = new Map<symbol, number>()
  const listeners = new Set<() => void>()
  let clearance = 0

  const publish = () => {
    clearance = Math.max(0, ...Array.from(elements.values()))
    listeners.forEach((listener) => listener())
  }

  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  }

  const getSnapshot = () => clearance

  const useRef = <T extends HTMLElement>(): RefCallback<T> => {
    const [element, setElement] = useState<T | null>(null)

    useEffect(() => {
      if (!element) return

      const id = Symbol('clearance')
      const measure = () => {
        elements.set(id, measureElement(element))
        publish()
      }

      measure()
      const observer = new ResizeObserver(measure)
      observer.observe(element)

      return () => {
        observer.disconnect()
        elements.delete(id)
        publish()
      }
    }, [element])

    return setElement
  }

  const useClearance = () => useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  return { useRef, useClearance }
}
